import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

const errorMessageVariants = cva(
  "rounded-md border p-4",
  {
    variants: {
      variant: {
        error: "border-red-200 bg-red-50",
        warning: "border-yellow-200 bg-yellow-50",
        info: "border-blue-200 bg-blue-50",
      },
    },
    defaultVariants: {
      variant: "error",
    },
  }
);

const errorTextVariants = cva(
  "text-sm",
  {
    variants: {
      variant: {
        error: "text-red-800",
        warning: "text-yellow-800",
        info: "text-blue-800",
      },
    },
    defaultVariants: {
      variant: "error",
    },
  }
);

export interface ErrorMessageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof errorMessageVariants> {
  error?: Error | { message?: string } | string | null;
  message?: string;
  errorMap?: Record<string, string>;
}

const ErrorMessage = forwardRef<HTMLDivElement, ErrorMessageProps>(
  ({ className, variant, error, message, errorMap, ...props }, ref) => {
    if (!error && !message) {
      return null;
    }

    let errorMessage = message;
    
    if (!errorMessage && error) {
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = error.message ?? "An error occurred";
      } else {
        errorMessage = "An error occurred";
      }
    }

    if (errorMap && errorMessage) {
      errorMessage = errorMap[errorMessage] ?? errorMessage;
    }

    return (
      <div
        ref={ref}
        className={cn(errorMessageVariants({ variant, className }))}
        role="alert"
        {...props}
      >
        <p className={cn(errorTextVariants({ variant }))}>
          {errorMessage}
        </p>
      </div>
    );
  }
);

ErrorMessage.displayName = "ErrorMessage";

export { ErrorMessage, errorMessageVariants };

